import { Injectable, computed, inject, signal } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { filter, Observable } from "rxjs";

import { ApiSessionService } from "../../api-session.service";
import { OrganizationService } from "../organization/organization.service";
import { ApiAddAction, ApiBrowseAction, ApiDeleteAction, ApiEditAction } from "../../api-actions";
import { extractData } from "../../apiResponse/response-body-new";
import {
  IApiError as ApiError,
  genericApiAction,
  onsipApiBrowse
} from "../../onsip-api-action-new";
import { arrayToRecord } from "../../util/arrayToRecord";
import { ParameterValue } from "../../util/api-action-description";
import { ResourceState, initialResourceState } from "../../resource-state";

import { APIUserAddress, UserAddress, apiUserAddressToUserAddress as clean } from "./user-address";
export { UserAddress };

@Injectable({ providedIn: "root" })
export class UserAddressService {
  protected readonly session = inject(ApiSessionService);
  private readonly orgService = inject(OrganizationService);

  private readonly _state = signal<ResourceState<UserAddress>>(initialResourceState<UserAddress>());

  readonly state = this._state.asReadonly();
  readonly addresses = computed((): Array<UserAddress> => Object.values(this._state().data));
  readonly orgAddresses = computed((): Array<UserAddress> => {
    const org = this.orgService.selfOrganization();
    if (!org) return [];
    return this.addresses().filter(a => a.domain === org.domain);
  });
  readonly selfAddresses = computed((): Array<UserAddress> => {
    const selfUserId = this.session.userId();
    if (!selfUserId) return [];
    return this.addresses().filter(a => a.userId === selfUserId);
  });
  readonly selfPrimaryAddress = computed(
    (): UserAddress | undefined =>
      [...this.selfAddresses()].sort((a, b) =>
        parseInt(a.userAddressId) < parseInt(b.userAddressId) ? -1 : 1
      )[0]
  );

  private readonly orgAddresses$ = toObservable(this.orgAddresses);
  private readonly selfAddresses$ = toObservable(this.selfAddresses);
  private readonly addresses$ = toObservable(this.addresses);
  private readonly selfPrimaryAddress$ = toObservable(this.selfPrimaryAddress).pipe(
    filter((a): a is UserAddress => !!a)
  );

  get orgState(): Observable<Array<UserAddress>> {
    return this.orgAddresses$;
  }

  get selfUser(): Observable<Array<UserAddress>> {
    return this.selfAddresses$;
  }

  get primaryUserAddress(): Observable<UserAddress> {
    return this.selfPrimaryAddress$;
  }

  get currentOrganizationUserAddresses(): Observable<Array<UserAddress>> {
    return this.orgAddresses$;
  }

  get accountState(): Observable<Array<UserAddress>> {
    return this.addresses$;
  }

  dispose(): void {
    this._state.set(initialResourceState<UserAddress>());
  }

  removeByUserId(userId: string): void {
    this._state.update(s => {
      const data = { ...s.data };
      Object.values(data).forEach(a => {
        if (a.userId === userId) delete data[a.userAddressId];
      });
      return { ...s, data };
    });
  }

  async userAddressBrowse(extraParams: Record<string, ParameterValue> = {}): Promise<void> {
    this._state.update(s => ({ ...s, loading: true, errors: [] }));
    try {
      const pages = await onsipApiBrowse({
        Action: ApiBrowseAction.UserAddressBrowse,
        SessionId: this.session.sessionId(),
        UserId: this.session.userId(),
        Limit: 2500,
        Orderby: "UserAddressId",
        ...extraParams
      });
      const items = pages.flatMap(page =>
        extractData<Array<APIUserAddress>>(
          page,
          ApiBrowseAction.UserAddressBrowse,
          "UserAddress",
          "UserAddresses"
        ).map(clean)
      );
      this._state.update(s => ({
        ...s,
        loading: false,
        loaded: true,
        data: { ...s.data, ...arrayToRecord(items, "userAddressId") }
      }));
    } catch (e: unknown) {
      this._state.update(s => ({ ...s, loading: false, errors: [...s.errors, e as ApiError] }));
      throw e;
    }
  }

  async userAddressBrowseWithOrgId(
    extraParams: Record<string, ParameterValue> = {}
  ): Promise<void> {
    this._state.update(s => ({ ...s, loading: true, errors: [] }));
    try {
      const pages = await onsipApiBrowse({
        Action: ApiBrowseAction.UserAddressBrowse,
        SessionId: this.session.sessionId(),
        OrganizationId: this.session.organizationId(),
        OrderBy: "UserAddressId",
        ...extraParams
      });
      const items = pages.flatMap(page =>
        extractData<Array<APIUserAddress>>(
          page,
          ApiBrowseAction.UserAddressBrowse,
          "UserAddress",
          "UserAddresses"
        ).map(clean)
      );
      this._state.update(s => ({
        ...s,
        loading: false,
        loaded: true,
        data: { ...s.data, ...arrayToRecord(items, "userAddressId") }
      }));
    } catch (e: unknown) {
      this._state.update(s => ({ ...s, loading: false, errors: [...s.errors, e as ApiError] }));
      throw e;
    }
  }

  async userAddressBrowseWithAccountId(
    extraParams: Record<string, ParameterValue> = {}
  ): Promise<void> {
    this._state.update(s => ({ ...s, loading: true, errors: [] }));
    try {
      const pages = await onsipApiBrowse({
        Action: ApiBrowseAction.UserAddressBrowse,
        SessionId: this.session.sessionId(),
        AccountId: this.session.accountId(),
        OrderBy: "UserAddressId",
        Limit: 25000,
        ...extraParams
      });
      const items = pages.flatMap(page =>
        extractData<Array<APIUserAddress>>(
          page,
          ApiBrowseAction.UserAddressBrowse,
          "UserAddress",
          "UserAddresses"
        ).map(clean)
      );
      this._state.update(s => ({
        ...s,
        loading: false,
        loaded: true,
        data: { ...s.data, ...arrayToRecord(items, "userAddressId") }
      }));
    } catch (e: unknown) {
      this._state.update(s => ({ ...s, loading: false, errors: [...s.errors, e as ApiError] }));
      throw e;
    }
  }

  async userAddressEdit(extraParams: Record<string, ParameterValue> = {}): Promise<UserAddress> {
    this._state.update(s => ({ ...s, loading: true, errors: [] }));
    try {
      const response = await genericApiAction({
        Action: ApiEditAction.UserAddressEdit,
        SessionId: this.session.sessionId(),
        ...extraParams
      });
      const item = clean(
        extractData<APIUserAddress>(response, ApiEditAction.UserAddressEdit, "UserAddress")
      );
      this._state.update(s => ({
        ...s,
        loading: false,
        data: { ...s.data, [item.userAddressId]: item }
      }));
      return item;
    } catch (e: unknown) {
      this._state.update(s => ({ ...s, loading: false, errors: [...s.errors, e as ApiError] }));
      throw e;
    }
  }

  async userAddressEditDefaultAddress(
    extraParams: Record<string, ParameterValue> = {}
  ): Promise<UserAddress> {
    this._state.update(s => ({ ...s, loading: true, errors: [] }));
    try {
      const response = await genericApiAction({
        Action: ApiEditAction.UserAddressEditDefaultAddress,
        SessionId: this.session.sessionId(),
        UserId: this.session.userId(),
        ...extraParams
      });
      const item = clean(
        extractData<APIUserAddress>(
          response,
          ApiEditAction.UserAddressEditDefaultAddress,
          "UserAddress"
        )
      );
      this._state.update(s => ({
        ...s,
        loading: false,
        data: { ...s.data, [item.userAddressId]: item }
      }));
      return item;
    } catch (e: unknown) {
      this._state.update(s => ({ ...s, loading: false, errors: [...s.errors, e as ApiError] }));
      throw e;
    }
  }

  async userAddressEditDoNotDisturb(
    extraParams: Record<string, ParameterValue> = {}
  ): Promise<UserAddress> {
    this._state.update(s => ({ ...s, loading: true, errors: [] }));
    try {
      const response = await genericApiAction({
        Action: ApiEditAction.UserAddressEditDoNotDisturb,
        SessionId: this.session.sessionId(),
        ...extraParams
      });
      const item = clean(
        extractData<APIUserAddress>(
          response,
          ApiEditAction.UserAddressEditDoNotDisturb,
          "UserAddress"
        )
      );
      this._state.update(s => ({
        ...s,
        loading: false,
        data: { ...s.data, [item.userAddressId]: item }
      }));
      return item;
    } catch (e: unknown) {
      this._state.update(s => ({ ...s, loading: false, errors: [...s.errors, e as ApiError] }));
      throw e;
    }
  }

  async userAddressAdd(
    Username: string,
    Name: string,
    UserId: string,
    Timeout = 30,
    extraParams: Record<string, ParameterValue> = {}
  ): Promise<UserAddress> {
    this._state.update(s => ({ ...s, loading: true, errors: [] }));
    try {
      const response = await genericApiAction({
        Action: ApiAddAction.UserAddressAdd,
        SessionId: this.session.sessionId(),
        OrganizationId: this.session.organizationId(),
        Domain: this.orgService.selfOrganization()?.domain ?? "",
        UserId,
        Username,
        Name,
        Timeout,
        ...extraParams
      });
      const item = clean(
        extractData<APIUserAddress>(response, ApiAddAction.UserAddressAdd, "UserAddress")
      );
      this._state.update(s => ({
        ...s,
        loading: false,
        loaded: true,
        data: { ...s.data, [item.userAddressId]: item }
      }));
      return item;
    } catch (e: unknown) {
      this._state.update(s => ({ ...s, loading: false, errors: [...s.errors, e as ApiError] }));
      throw e;
    }
  }

  async userAddressDelete(
    Address: string,
    UserAddressId: string,
    extraParams: Record<string, ParameterValue> = {}
  ): Promise<void> {
    this._state.update(s => ({ ...s, loading: true, errors: [] }));
    try {
      await genericApiAction({
        Action: ApiDeleteAction.UserAddressDelete,
        SessionId: this.session.sessionId(),
        Address,
        UserAddressId,
        ...extraParams
      });
      this._state.update(s => {
        const data = { ...s.data };
        delete data[UserAddressId];
        return { ...s, loading: false, data };
      });
    } catch (e: unknown) {
      this._state.update(s => ({ ...s, loading: false, errors: [...s.errors, e as ApiError] }));
      throw e;
    }
  }
}
